import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { CircularProgress, Typography } from '@mui/material';
import dropboxService from '../utils/dropboxService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--al-bg);
  color: white;
  padding: 20px;
`;

const Card = styled.div`
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  max-width: 500px;
`;

const StatusIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
`;

const DropboxCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Connecting to Dropbox...');

  useEffect(() => {
    const handleCallback = async () => {
      // Get the authorization code from URL
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const state = searchParams.get('state');

      // Check for errors from Dropbox
      if (error) {
        setStatus('error');
        setMessage(`Authorization failed: ${errorDescription || error}`);
        return;
      }

      // Verify we have a code
      if (!code) {
        setStatus('error');
        setMessage('No authorization code received from Dropbox.');
        return;
      }

      // Verify state token (CSRF protection)
      const storedState = localStorage.getItem('dropbox_oauth_state');
      if (state && storedState && state !== storedState) {
        setStatus('error');
        setMessage('Invalid state token. Please try connecting again.');
        return;
      }

      try {
        setMessage('Exchanging authorization code...');
        
        // Exchange the code for tokens
        const redirectUri = `${window.location.origin}/auth/dropbox/callback`;
        const result = await dropboxService.completeOAuthFlow(code, redirectUri);

        if (result.success) {
          setStatus('success');
          setMessage('Dropbox connected successfully!');
          
          // Redirect back to invoicing page after a short delay
          setTimeout(() => {
            navigate('/invoicing', { 
              state: { dropboxConnected: true } 
            });
          }, 2000);
        } else {
          throw new Error('Failed to complete authorization');
        }
      } catch (err) {
        console.error('Error completing Dropbox OAuth:', err);
        setStatus('error');
        setMessage(`Failed to connect Dropbox: ${err.message}`);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <Container>
      <Card>
        <StatusIcon>
          {status === 'processing' && '📦'}
          {status === 'success' && '✅'}
          {status === 'error' && '❌'}
        </StatusIcon>
        
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {status === 'processing' && 'Connecting Dropbox'}
          {status === 'success' && 'Connected!'}
          {status === 'error' && 'Connection Failed'}
        </Typography>
        
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
          {message}
        </Typography>
        
        {status === 'processing' && (
          <CircularProgress sx={{ color: 'var(--al-primary)' }} />
        )}
        
        {status === 'success' && (
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Redirecting to Invoicing...
          </Typography>
        )}
        
        {status === 'error' && (
          <button
            onClick={() => navigate('/invoicing')}
            style={{
              background: 'var(--al-primary)',
              color: '#222',
              border: 'none',
              borderRadius: '4px',
              padding: '12px 24px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              textTransform: 'uppercase'
            }}
          >
            Back to Invoicing
          </button>
        )}
      </Card>
    </Container>
  );
};

export default DropboxCallback;
